<template>
  <section class="hero-section" :style="{ minHeight: heroHeight, paddingTop: heroTopOffset }">
    <div class="hero-content">
      <Motion
        :initial="{ opacity: 0, y: 30 }"
        :animate="{ opacity: 1, y: 0 }"
        :transition="{ duration: 0.8, ease: 'easeOut' }"
      >
        <h1 class="hero-headline">
          Industry-Integrated Learning<br />
          <span class="gradient-text">for Future Engineers</span>
        </h1>
      </Motion>

      <Motion
        :initial="{ opacity: 0, y: 30 }"
        :animate="{ opacity: 1, y: 0 }"
        :transition="{ duration: 0.8, delay: 0.15, ease: 'easeOut' }"
      >
        <p class="hero-subheading">
          Partnering with leading universities to help students gain industry-relevant skills,
          build real-world projects, earn certifications, and accelerate their career readiness alongside their degree.
        </p>
      </Motion>

      <Motion
        :initial="{ opacity: 0, y: 30 }"
        :animate="{ opacity: 1, y: 0 }"
        :transition="{ duration: 0.8, delay: 0.3, ease: 'easeOut' }"
      >
        <div class="hero-cta">
          <button class="btn-primary">
            <span>Explore Programs</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M5 12h14" />
              <path d="m12 5 7 7-7 7" />
            </svg>
          </button>
          <button class="btn-secondary">
            Book Career Counseling
          </button>
        </div>
      </Motion>

      <Motion
        :initial="{ opacity: 0, y: 30, scale: 0.95 }"
        :animate="{ opacity: 1, y: 0, scale: 1 }"
        :transition="{ duration: 0.8, delay: 0.45, ease: 'easeOut' }"
      >
        <div ref="statsRef" class="hero-stats">
          <div class="stat-item">
            <div class="stat-value">{{ formatStat(displayCounts[0], stats[0].divisor) }}</div>
            <div class="stat-label">Hiring Partners</div>
          </div>
          <div class="stat-divider"></div>
          <div class="stat-item">
            <div class="stat-value">{{ formatStat(displayCounts[1], stats[1].divisor) }}</div>
            <div class="stat-label">Students Placed</div>
          </div>
          <div class="stat-divider"></div>
          <div class="stat-item">
            <div class="stat-value">{{ formatStat(displayCounts[2], stats[2].divisor) }}</div>
            <div class="stat-label">Cities Across India</div>
          </div>
        </div>
      </Motion>
    </div>
  </section>
</template>

<script setup lang="ts">
const heroHeight = ref('100vh')
const heroTopOffset = ref('80px')

const updateHeight = () => {
  const navbar = document.querySelector('.navbar') as HTMLElement
  // Use real navbar height + small breathing room. Falls back to 80 on SSR.
  const navbarHeight = navbar ? navbar.getBoundingClientRect().height : 80
  const safeOffset = Math.ceil(navbarHeight + 16)
  heroTopOffset.value = `${safeOffset}px`
  // Total section height keeps viewport coverage; content area starts below the navbar.
  heroHeight.value = `${window.innerHeight}px`
}

// ── Stats count-up animation ──────────────────────────────────────────────────
interface StatConfig {
  target: number
  divisor: number
}

const stats: StatConfig[] = [
  { target: 5200, divisor: 1 },   // 5200+
  { target: 487, divisor: 100 },  // 4.87L+
  { target: 90, divisor: 1 },     // 90+
]

const statsRef = ref<HTMLElement | null>(null)
const displayCounts = ref<number[]>(stats.map(() => 0))
const hasAnimated = ref(false)

function formatStat(value: number, divisor: number): string {
  if (divisor === 1) return `${value}+`
  return `${(value / divisor).toFixed(2)}L+`
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3)
}

function animateStat(index: number, target: number, duration: number) {
  const start = performance.now()

  function tick(now: number) {
    const elapsed = now - start
    const progress = Math.min(elapsed / duration, 1)
    const eased = easeOutCubic(progress)
    displayCounts.value[index] = Math.round(eased * target)
    if (progress < 1) requestAnimationFrame(tick)
  }
  requestAnimationFrame(tick)
}

onMounted(() => {
  if (process.client) {
    setTimeout(updateHeight, 100)
    window.addEventListener('resize', updateHeight)

    // Trigger stats count-up when in view
    if (statsRef.value) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && !hasAnimated.value) {
            hasAnimated.value = true
            stats.forEach((stat, i) => {
              animateStat(i, stat.target, 1600)
            })
            observer.disconnect()
          }
        },
        { threshold: 0.3 }
      )
      observer.observe(statsRef.value)
    }
  }
})

onBeforeUnmount(() => {
  if (process.client) {
    window.removeEventListener('resize', updateHeight)
  }
})
</script>

<style scoped>
.hero-section {
  /* padding-top is set inline from JS using real navbar height + 16px breathing room */
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  padding-top: 96px; /* fallback; overridden inline on mount */
  position: relative;
  z-index: 10;
}

.hero-content {
  max-width: 1000px;
  text-align: center;
  position: relative;
  /* Vignette so text always has a consistent dark surface behind it,
     even when the Vanta globe bright spots are centered behind the hero. */
  padding: 2rem 1.5rem;
  border-radius: 24px;
  background: radial-gradient(
    ellipse at center,
    rgba(0, 0, 0, 0.55) 0%,
    rgba(0, 0, 0, 0.35) 45%,
    rgba(0, 0, 0, 0) 75%
  );
}

[data-theme="light"] .hero-content {
  background: radial-gradient(
    ellipse at center,
    rgba(255, 255, 255, 0.65) 0%,
    rgba(255, 255, 255, 0.35) 50%,
    rgba(255, 255, 255, 0) 80%
  );
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1.25rem;
  background: rgba(255, 120, 0, 0.1);
  border: 1px solid rgba(255, 120, 0, 0.3);
  border-radius: 50px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #ff7800;
  margin-bottom: 2rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.badge-dot {
  width: 8px;
  height: 8px;
  background: #ff7800;
  border-radius: 50%;
  animation: pulse 2s infinite;
}

@keyframes pulse {

  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }

  50% {
    opacity: 0.5;
    transform: scale(0.8);
  }
}

.hero-headline {
  font-size: clamp(2.5rem, 6vw, 4.5rem);
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 1.5rem;
  color: var(--text-primary);
  letter-spacing: -0.02em;
}

.gradient-text {
  background: linear-gradient(135deg, #ff7800, #ff7800);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-subheading {
  font-size: 1.25rem;
  /* Use a brighter, near-white tone so the subheading stays readable over the
     dark Vanta globe canvas in dark mode (--text-secondary at 0.8 opacity
     gets lost against the animated background). */
  color: rgba(255, 255, 255, 0.965);
  max-width: 700px;
  margin: 0 auto 2.5rem;
  line-height: 1.7;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.793);
  background-color: #ffffff04;
}

[data-theme="light"] .hero-subheading {
  color: #1f2937;
  text-shadow: none;
}

.hero-cta {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 4rem;
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #ff7800, #ff4e00);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(255, 120, 0, 0.3);
}

.btn-secondary {
  padding: 1rem 2rem;
  background: transparent;
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

[data-theme="dark"] .btn-secondary {
  border-color: rgba(255, 255, 255, 0.3);
}

.btn-secondary:hover {
  background: var(--glass-bg);
  border-color: var(--accent-orange);
}

.hero-stats {
  display: flex;
  align-items: center;
  gap: 2rem;
  padding: 2rem 3rem;
  background: var(--glass-bg);
  border: 1px solid var(--glass-border);
  border-radius: 16px;
  backdrop-filter: blur(10px);
  margin: 0 auto;
  max-width: fit-content;
  transition: background 0.3s ease, border-color 0.3s ease;
}

[data-theme="light"] .hero-stats {
  box-shadow: var(--shadow-card);
}

.stat-item {
  text-align: center;
}

.stat-value {
  font-size: 2rem;
  font-weight: 900;
  color: var(--accent-orange);
  margin-bottom: 0.25rem;
}

.stat-label {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.78);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

[data-theme="light"] .stat-label {
  color: #4b5563;
  text-shadow: none;
}

.stat-divider {
  width: 1px;
  height: 40px;
  background: var(--border-color);
}

@media (max-width: 768px) {
  /* Give the hero room for stacked content on short viewports so the headline
     never sits under the navbar. navbar(~64px) + headline + sub + 2 buttons + 3 stats. */
  .hero-section {
    min-height: 100dvh;
    justify-content: flex-start;
  }

  .hero-headline {
    font-size: clamp(2rem, 8vw, 2.75rem);
    margin-bottom: 1rem;
  }

  .hero-subheading {
    font-size: 1rem;
    margin-bottom: 1.75rem;
  }

  .hero-cta {
    margin-bottom: 2.5rem;
  }

  .hero-stats {
    flex-direction: column;
    gap: 1.25rem;
    padding: 1.25rem 1.5rem;
    width: 100%;
  }

  .stat-divider {
    width: 40px;
    height: 1px;
  }

  .hero-cta {
    flex-direction: column;
    align-items: center;
  }

  .btn-primary,
  .btn-secondary {
    width: 100%;
    max-width: 300px;
    justify-content: center;
  }
}
</style>
